import { useEffect, useRef, useState } from "react";

// STYLES
import {
  BackToGuidesButton,
  BackToGuidesImage,
  BackToGuidesWrapper,
  TitleWrapper,
  LegislationDetailContainer,
  ContentWrapper,
  SubtitleWrapper,
  NoDataContainer,
  SvgContainer,
} from "./LegislationDetailsStyled";

// LIBRARIES
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

//ASSETS
import LeftArrow from "assets/icons/ArrowLeftHovered.svg";
import { ReactComponent as NoDataDark } from "assets/svg/NoDataDark.svg";

// MISC
import { COLORS } from "constants/variables";
import { TextType } from "components/Atoms/CustomText/CustomTextModel";
import { appRoutes } from "config/AppRoutesConfig";

// REDUX
import { useSelector } from "react-redux";
import { accessibilityBiggerCursorSelector } from "slices/UISlice/uiSliceSelectors";

// COMPONENTS
import CustomText from "components/Atoms/CustomText/CustomText";
import ParagraphAtom from "components/Atoms/ParagraphAtom/ParagraphAtom";
import HeaderTab from "components/HeaderTab/HeaderTab";
import Pagination from "components/Atoms/Paginations/Pagination";

// HOOKS
import usePagination from "hooks/usePagination";
import newsAndMediaService from "services/newsAndMediaService";

const LegislationDetails = () => {
  // PROPERTIES

  // API REQUESTS

  // LIBRARY CONSTANTS
  const navigate = useNavigate();
  const biggerCursor = useSelector(accessibilityBiggerCursorSelector);
  const { t } = useTranslation();
  const topRef = useRef(null);
  // STATE CONSTANTS
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  //eslint-disable-next-line
  const [totalPages, setTotalPages] = useState<number>(10);
  const [otherLegislations, setOtherLegislations] = useState<any>();
  const [organizationLegislations, setOrganizationLegislations] =
    useState<any>();
  const [combinedData, setCombinedData] = useState([]);
  const pageSize = 6;

  const paginatedData = combinedData.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    topRef.current.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };
  useEffect(() => {
    const fetchData = async () => {
      try {
        const type =
          selectedTab === 0
            ? "primara"
            : selectedTab === 1
            ? "secundara"
            : "internationala";

        const [otherResponse, organizationResponse] = await Promise.all([
          newsAndMediaService.getLegislations(
            "legislatie",
            type,
            "other",
            1,
            1000
          ),
          newsAndMediaService.getLegislations(
            "legislatie",
            type,
            "organization",
            1,
            1000
          ),
        ]);

        const otherData = otherResponse.data;
        const organizationData = organizationResponse.data;

        let combinedData = [];

        if (otherData?.length > 0) {
          otherData[0] = {
            ...otherData[0],
            subtitle: t("view.legislationDetail.subtitles.normative"),
          };
        }
        if (organizationData?.length > 0) {
          organizationData[0] = {
            ...organizationData[0],
            subtitle: t("view.legislationDetail.subtitles.organisation"),
          };
        }

        if (otherData.length > 0) {
          // combinedData.push({ type: "subtitle", text: t("view.legislationDetail.subtitles.normative") });
          combinedData = combinedData.concat(
            otherData.map((item) => ({ ...item, source: "other" }))
          );
        }

        if (organizationData.length > 0) {
          // combinedData.push({ type: "subtitle", text: t("view.legislationDetail.subtitles.organisation") });
          combinedData = combinedData.concat(
            organizationData.map((item) => ({
              ...item,
              source: "organization",
            }))
          );
        }

        setCombinedData(combinedData);
        setTotalPages(Math.ceil(combinedData.length / pageSize));
      } catch (err) {
        console.error(err);
      }
    };

    fetchData();
  }, [selectedTab, t]);

  const content = (
    <ContentWrapper>
      {paginatedData.length > 0 ? (
        paginatedData.map((item, index) => {
          if (item.subtitle) {
            return (
              <SubtitleWrapper key={`subtitle-${index}`}>
                <CustomText
                  text={item.subtitle}
                  type={TextType.H4}
                  color={COLORS.Gunmetal}
                />
                <ParagraphAtom
                  key={index}
                  text={item?.attributes?.title}
                  href={item?.attributes?.doc_link}
                />
              </SubtitleWrapper>
            );
          } else {
            return (
              <ParagraphAtom
                key={index}
                text={item?.attributes?.title}
                href={item?.attributes?.doc_link}
              />
            );
          }
        })
      ) : (
        <NoDataContainer>
          <SvgContainer>
            <NoDataDark />
          </SvgContainer>
          <CustomText
            text={t("app.noData")}
            type={TextType.H4}
            color={COLORS.MetalicSilver}
          />
        </NoDataContainer>
      )}
    </ContentWrapper>
  );

  const tabs = [
    {
      id: 0,
      tabTitle: t("view.legislationDetail.tabs.primaryLegislation"),
      content: content,
      totalPages: totalPages,
      currentPage: currentPage,
      handlePageChange: handlePageChange,
    },
    {
      id: 1,
      tabTitle: t("view.legislationDetail.tabs.secondaryLegislation"),
      content: content,
      totalPages: totalPages,
      currentPage: currentPage,
      handlePageChange: handlePageChange,
    },
    {
      id: 2,
      tabTitle: t("view.legislationDetail.tabs.internationalLegislation"),
      content: content,
      totalPages: totalPages,
      currentPage: currentPage,
      handlePageChange: handlePageChange,
    },
  ];

  // LIFE CYCLE

  // EVENT HANDLERS
  const handleTabSelect = (selectedTabId: number) => {
    setSelectedTab(selectedTabId);
    setCurrentPage(1);
  };

  return (
    <LegislationDetailContainer ref={topRef}>
      <TitleWrapper>
        <BackToGuidesWrapper
          biggerCursor={biggerCursor}
          onClick={() => navigate(appRoutes.legislation)}
        >
          <BackToGuidesImage src={LeftArrow} alt="backIcon" />
          <BackToGuidesButton>
            <CustomText
              text={t("view.legislationDetail.button")}
              type={TextType.H5boldspaced}
              color={COLORS.RaisinBlack}
            />
          </BackToGuidesButton>
        </BackToGuidesWrapper>
        <CustomText
          type={TextType.H2}
          text={t("view.legislationDetail.title")}
        />
      </TitleWrapper>
      <HeaderTab tabs={tabs} onTabSelect={handleTabSelect} />
      {tabs[selectedTab].content}
      {totalPages > 1 && (
        <Pagination
          totalPages={totalPages}
          currentPage={currentPage}
          onPageChange={handlePageChange}
        />
      )}
    </LegislationDetailContainer>
  );
};

export default LegislationDetails;
